import { useEffect, useMemo, useState } from 'react';
import '../css/ApacheSuperset.css';
import api from '../services/api';

const EMPTY_DATA = {
  casosRegistrados: 0,
  casosNuevos: 0,
  recaidas: 0,
  casosActivos: 0,
  casosCerrados: 0,
  incidenciaAcumulada: null,
  tendenciaSemanal: [],
  distribucionEtaria: [],
  tiposEvento: [],
  clasificacionProgramatica: [],
  resultadosProgramaticos: [],
  cierreEvento: [],
};

const RealHorizontalBars = ({ items, labelKey, tone = 'teal' }) => {
  const max = Math.max(1, ...items.map((item) => item.casos));
  if (!items.length) return <p className="real-bars-empty">Sin datos para los filtros seleccionados</p>;
  return <div className={`real-horizontal-bars real-horizontal-bars--${tone}`}>
    {items.map((item) => <div className="real-horizontal-row" key={item[labelKey]}>
      <span title={item[labelKey]}>{item[labelKey]}</span>
      <i style={{ width: `${Math.max(4, (item.casos * 100) / max)}%` }}><b>{item.casos}</b></i>
    </div>)}
  </div>;
};

const SupersetSituationView = ({ pathology, scope, appliedFilters, catalogOptions, onNavigate, summaryMode = false }) => {
  const [data, setData] = useState(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const params = useMemo(() => {
    const findByName = (items, name, idField) => {
      if (!name || /^Tod[oa]s$/i.test(name)) return null;
      const item = (items || []).find(
        (option) => String(option.Nombre).toLocaleLowerCase() === String(name).toLocaleLowerCase()
      );
      return item?.[idField] || null;
    };
    return {
      idPatologia: scope?.idPatologia,
      idZonaSanitaria: scope?.idZonaSanitaria,
      idAreaProgramatica: scope?.idAreaProgramatica,
      anio: appliedFilters?.anio || undefined,
      semanas: Array.isArray(appliedFilters?.semana)
        ? appliedFilters.semana.join(',') || undefined
        : appliedFilters?.semana || undefined,
      provincia: appliedFilters?.provincia || undefined,
      departamentos: Array.isArray(appliedFilters?.departamento)
        ? appliedFilters.departamento.join(',') || undefined
        : appliedFilters?.departamento || undefined,
      municipios: Array.isArray(appliedFilters?.municipio)
        ? appliedFilters.municipio.join(',') || undefined
        : appliedFilters?.municipio || undefined,
      idSector: findByName(catalogOptions?.sectores, appliedFilters?.sector, 'IdSector'),
      idSubsector: findByName(catalogOptions?.subsectores, appliedFilters?.subsector, 'IdSubsector'),
      idInstitucion: findByName(
        catalogOptions?.instituciones,
        appliedFilters?.efector,
        'IdInstitucionSeguimientoTratamiento'
      ),
      idEstadoCaso: findByName(catalogOptions?.estados, appliedFilters?.estadoCaso, 'IdEstado'),
      idEstadoSeguimiento: findByName(
        catalogOptions?.estados,
        appliedFilters?.estadoSeguimiento,
        'IdEstado'
      ),
    };
  }, [scope, appliedFilters, catalogOptions]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/analytics/situation', { params })
      .then(({ data: response }) => {
        if (active) setData(response);
      })
      .catch(() => {
        if (active) setError('No se pudieron cargar los indicadores reales.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [params]);

  const total = data.casosRegistrados || 0;
  const percent = (value) => total > 0 ? `${((value * 100) / total).toFixed(1)}% del total` : 'Sin casos';
  const kpis = [
    ['Casos nuevos', data.casosNuevos, 'Casos únicos notificados', 'blue'],
    ['Recaídas', data.recaidas, percent(data.recaidas), 'purple'],
    ['Casos activos', data.casosActivos, 'En tratamiento / seguimiento', 'cyan'],
    ['Casos cerrados', data.casosCerrados, 'Con resultado o tratamiento finalizado', 'green'],
    [
      'Incidencia acumulada',
      data.incidenciaAcumulada == null ? '—' : data.incidenciaAcumulada.toLocaleString('es-AR'),
      data.incidenciaAcumulada == null ? 'Sin población disponible' : 'Por 100.000 habitantes',
      'orange',
    ],
  ];
  const trend = data.tendenciaSemanal || [];
  const trendMax = Math.max(1, ...trend.flatMap((item) => [item.casos, item.recaidas]));
  const trendPoints = (field) => trend.map((item) => {
    const x = 45 + ((item.semana - 1) / 52) * 555;
    const y = 195 - (Number(item[field] || 0) / trendMax) * 160;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const ageDistribution = data.distribucionEtaria || [];
  const ageMax = Math.max(1, ...ageDistribution.map((item) => item.casos));
  const eventTypes = data.tiposEvento || [];
  const eventTotal = eventTypes.reduce((sum, item) => sum + item.casos, 0);
  const eventColors = ['#1684d4', '#7549c4', '#f29527', '#73879a', '#20a8a8'];
  let eventOffset = 0;
  const donutBackground = eventTotal > 0
    ? `conic-gradient(${eventTypes.map((item, index) => {
        const start = eventOffset;
        eventOffset += (item.casos * 100) / eventTotal;
        return `${eventColors[index % eventColors.length]} ${start}% ${eventOffset}%`;
      }).join(', ')})`
    : '#dce7ee';

  return (
    <section className="situation-view">
      <nav className="situation-view__tabs">
        {[
          ['Resumen', 'dashboard'], ['Situación', 'situation'], ['Contactos', 'contacts'],
          ['Territorio', 'territorial'], ['Sector/Efector', 'sector'], ['Calidad', 'quality'],
          ['Patología', 'pathology'], ['Descarga', 'download'],
        ].map(([tab, view]) => (
          <button
            className={tab === (summaryMode ? 'Resumen' : 'Situación') ? 'is-active' : ''}
            type="button"
            key={tab}
            onClick={() => onNavigate?.(view)}
          >
            {tab}
          </button>
        ))}
      </nav>
      <div className="situation-view__body">
        <h1 className="situation-view__title">{summaryMode ? 'Resumen ejecutivo' : 'Situación epidemiológica'}</h1>
        {error && <p className="situation-view__error" role="alert">{error}</p>}
        <div className={`situation-view__kpis ${loading ? 'is-loading' : ''}`} aria-busy={loading}>
          {kpis.map(([label, value, detail, tone]) => (
            <article className={`situation-card situation-kpi situation-kpi--${tone}`} key={label}>
              <strong>{label}</strong><b>{loading ? '…' : value}</b><small>{loading ? 'Consultando VEC' : detail}</small>
            </article>
          ))}
        </div>
        <div className="situation-view__main-grid">
          <article className="situation-card situation-card--trend">
            <h2>Tendencia de casos por semana epidemiológica</h2><p>Casos nuevos y recaídas · {pathology || 'Patología activa'}</p>
            <svg viewBox="0 0 620 220" role="img" aria-label="Tendencia semanal de casos">
              {[35, 75, 115, 155, 195].map((y) => <line key={y} x1="45" x2="600" y1={y} y2={y} />)}
              {trend.length > 0 ? <>
                <polyline className="cases" points={trendPoints('casos')} />
                <polyline className="relapses" points={trendPoints('recaidas')} />
                <text x="45" y="214">SE {trend[0].semana}</text>
                <text x="555" y="214">SE {trend[trend.length - 1].semana}</text>
              </> : <text className="empty-chart" x="310" y="115">Sin datos para los filtros seleccionados</text>}
            </svg>
          </article>
          <article className="situation-card"><h2>Distribución por grupo etario</h2><p>Casos únicos por edad</p><div className="situation-bars">{ageDistribution.length > 0 ? ageDistribution.map((item) => <i key={item.grupo} title={`${item.grupo}: ${item.casos}`} style={{ height: `${Math.max(3, (item.casos * 100) / ageMax)}%` }}><b>{item.casos}</b><span>{item.grupo}</span></i>) : <em>Sin datos</em>}</div></article>
          <article className="situation-card situation-card--donut"><h2>Tipo de evento epidemiológico</h2><p>{pathology || 'Patología activa'}</p><div className="situation-donut" style={{ background: donutBackground }}><span><b>{eventTotal}</b>Total</span></div><ul>{eventTypes.length > 0 ? eventTypes.map((item, index) => <li key={item.tipo} style={{ '--event-color': eventColors[index % eventColors.length] }}>{item.tipo}: {item.casos}</li>) : <li>Sin datos para los filtros seleccionados</li>}</ul></article>
        </div>
        <div className="situation-view__lower-grid">
          <article className="situation-card"><h2>Clasificación inicio tratamiento</h2><p>Clasificación programática registrada al iniciar el tratamiento</p><RealHorizontalBars items={data.clasificacionProgramatica || []} labelKey="clasificacion" /></article>
          <article className="situation-card"><h2>Resultado del tratamiento</h2><p>Resultado programático, separado del estado operativo</p><RealHorizontalBars items={data.resultadosProgramaticos || []} labelKey="resultado" tone="green" /></article>
          <article className="situation-card situation-notes"><h2>Estado operativo y cierre</h2><p>No modifica el resultado epidemiológico</p><RealHorizontalBars items={data.cierreEvento || []} labelKey="estado" tone="blue" /><small className="situation-source">Fuente: {data.fuente || 'VEC'} · Último registro: {data.ultimaFechaRegistro ? new Date(data.ultimaFechaRegistro).toLocaleDateString('es-AR', { timeZone: 'UTC' }) : 'sin fecha'} · Consulta: {data.actualizadoEn ? new Date(data.actualizadoEn).toLocaleString('es-AR') : '—'}</small></article>
        </div>
      </div>
    </section>
  );
};

export default SupersetSituationView;
